import { useQuery, useSubscription } from "@apollo/client";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import { compact } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { Icon } from "../../Icon/Icon";
import { Button } from "../../SharedComponents/Button/Button";
import { SearchBox } from "../../SharedComponents/SearchBox/SearchBox";
import { AdminRightActionsContainer } from "../AdminRightActionsContainer/AdminRightActionsContainer";
import "./AdminCurrentAccountUsers.css";
import { AdminCurrentAccountUsersList } from "./AdminCurrentAccountUsersList/AdminCurrentAccountUsersList";

export const AdminCurrentAccountUsers = () => {
  const { t } = useTranslation();
  const id = Cookies.get("primary_account");
  const { data: currentUserData } = useQuery(getCurrentUserInformation);
  const history = useHistory();
  const { data, loading } = useQuery(getUsersOnAccountQuery, {
    variables: { accountIds: [id] },
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  useSubscription(subscribeToUserRevoked, {
    variables: { accountId: id },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) => {
      const prev = cache.readQuery({
        query: getUsersOnAccountQuery,
        variables: { accountIds: [id] },
      });

      cache.writeQuery({
        query: getUsersOnAccountQuery,
        variables: { accountIds: [id] },
        data: {
          ...prev,
          users: prev.users.filter(
            (x) =>
              x.id !== subscriptionData.data.onSSOUserRemovedFromAccount.userId
          ),
        },
      });
    },
  });

  // Handling searching
  useEffect(() => {
    if (data?.users && searchTerm !== "") {
      const filtered = data.users.map((usr) => {
        if (
          usr?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          usr?.email?.toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          return usr;
        } else {
          return null;
        }
      });

      const compacted = compact(filtered);

      setFilteredUsers(compacted);
    }

    if (data?.users && searchTerm === "") {
      setFilteredUsers(data.users);
    }
  }, [data, searchTerm]);

  const canEdit =
    currentUserData?.user?.roles?.sso?.find((x) => x.accountId === id)?.role ===
    "ADMIN";

  return (
    <div className="AdminCurrentAccountUsers">
      <div className="AdminDistributors__content">
        <AdminCurrentAccountUsersList
          users={filteredUsers}
          accountId={id}
          loading={loading}
          canEdit={canEdit}
        />
      </div>
      <div className="AdminDistributors__right">
        <div className="AdminDistributors__search">
          <SearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <div className="AdminDistributors__actions">
          <AdminRightActionsContainer>
            <div className="AdminDistributors__add-new">
              {canEdit && (
                <Button
                  buttonStyle="Button__primary"
                  click={() =>
                    history.push(`/admin/accounts/${id}/invite-user`)
                  }
                >
                  <Icon
                    className="AdminRightActionsContainer__add-new--icon"
                    name="iconAdd"
                    width={12}
                    height={12}
                  />
                  <div className="AdminRightActionsContainer__add-new--text">
                    {t("generic.addNew")}
                  </div>
                </Button>
              )}
            </div>
          </AdminRightActionsContainer>
        </div>
      </div>
    </div>
  );
};

const getUsersOnAccountQuery = gql`
  query users($accountIds: [String]) {
    users(accountIds: $accountIds) {
      id
      email
      name
      status
      roles
    }
  }
`;

const subscribeToUserRevoked = gql`
  subscription onSSOUserRemovedFromAccount($accountId: String) {
    onSSOUserRemovedFromAccount(accountId: $accountId)
  }
`;
